// Automation Rules Engine — service layer.
//
// Two responsibilities:
//   1. Read/write automationEvents (top-level Firestore collection).
//   2. Create the underlying tasks that automation rules generate, with the
//      extra fields the engine needs (type, automationRuleId, automationEventId,
//      pushHistory, etc.) without modifying the existing manual-task path.

import {
  collection, doc, addDoc, setDoc, updateDoc, getDocs, query, where,
  arrayUnion, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { newAutomationEventId } from '../utils/automationUtils';

// ── automationEvents ─────────────────────────────────────────

// Create an automationEvent document. Used both when a rule fires immediately
// (onEnter / autoRun) and when a rule will fire later (afterDays — Cloud
// Functions will own the trigger; we store the scheduled fire time here).
export async function createAutomationEvent({
  companyId,
  jobId,
  ruleId,
  stageAtTrigger,
  taskId = null,
  status = 'pending',
  nextFireAt = null,           // Date | null — only set for afterDays
  triggeredAt = null,          // Date | null — set when a task is actually created
}) {
  const eventId = newAutomationEventId();
  const ref = doc(db, 'automationEvents', eventId);
  await setDoc(ref, {
    companyId,
    jobId,
    ruleId,
    stageAtTrigger: stageAtTrigger || null,
    taskId,
    status,
    pushHistory: [],
    triggeredAt: triggeredAt ? Timestamp.fromDate(triggeredAt) : (status === 'pending' && !nextFireAt ? null : serverTimestamp()),
    nextFireAt: nextFireAt ? Timestamp.fromDate(nextFireAt) : null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return eventId;
}

export async function logAutomationEventPush(eventId, { pushedBy, reason }) {
  const ref = doc(db, 'automationEvents', eventId);
  const entry = {
    pushedAt: new Date(),
    pushedBy: pushedBy || null,
    reason: reason || '',
  };
  await updateDoc(ref, {
    pushHistory: arrayUnion(entry),
    status: 'pending',
    updatedAt: serverTimestamp(),
    // Cloud Functions: when this resets, set nextFireAt = now + daysThreshold.
    // We don't have the rule context here so the caller is expected to refresh
    // nextFireAt via setAutomationEventNextFireAt if applicable.
  });
}

export async function setAutomationEventNextFireAt(eventId, nextFireAt) {
  const ref = doc(db, 'automationEvents', eventId);
  await updateDoc(ref, {
    nextFireAt: nextFireAt ? Timestamp.fromDate(nextFireAt) : null,
    updatedAt: serverTimestamp(),
  });
}

export async function markAutomationEventCompleted(eventId) {
  const ref = doc(db, 'automationEvents', eventId);
  await updateDoc(ref, {
    status: 'completed',
    completedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function markAutomationEventDismissed(eventId) {
  const ref = doc(db, 'automationEvents', eventId);
  await updateDoc(ref, {
    status: 'dismissed',
    dismissedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

// NOTE: no call sites today — kept as the read side of the automationEvents
// model.
//
// PARKED, NOT DEAD (reviewed 2026-08-27). The collection this reads is
// LIVE — AutomationContext.js and the checkAfterDaysAutomations function
// write to it continuously — so this is a model with a write side and no
// read side yet, not an abandoned experiment. Its consumer is a per-job
// automation-history surface; the nearest scoped roadmap items are 5.26
// (Automated Stage Reminder System) and 5.29 (Needs Attention Widget),
// though neither spec names this function outright. The companyId-filter
// note below is the expensive lesson worth keeping regardless. The companyId filter is required rather than optional: firestore.rules
// scopes this collection to the caller's own company, and Firestore rejects a
// LIST query whose constraints don't prove that on their own (security rules are
// not filters). Filtering by jobId alone is denied outright. Two equality
// clauses need no composite index.
export async function getAutomationEventsForJob(companyId, jobId) {
  if (!companyId || !jobId) return [];
  const q = query(
    collection(db, 'automationEvents'),
    where('companyId', '==', companyId),
    where('jobId', '==', jobId),
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// ── Task creation ─────────────────────────────────────────

// Create a task that represents an automation firing. The shape is a strict
// superset of the existing manual-task shape so existing TaskPanel/Tasks page
// rendering still works:
//   - assignedTo: first selected user (so the existing single-assignee UI
//     resolves to a name and the existing where('assignedTo','==',uid)
//     subscription delivers the task to that user).
//   - assignedToIds: full list of selected userIds (Tasks page can later
//     fan-out via array-contains; today the first user is the primary
//     assignee).
//   - type: 'Automated' | 'Decision' (per spec; 'Manual' continues to be
//     unset on existing tasks).
//   - automationRuleId, automationEventId: link back to the rule and event.
//   - status / linkedType / linkedId: kept on the existing convention
//     ('open'/'complete', 'job'/'ticket'/'client') so existing queries work.
export async function createAutomationTask({
  companyId,
  jobId,
  jobName,
  ruleId,
  eventId,
  taskType,             // 'standard' | 'decision'
  description,
  assignedToIds,        // array of uids
  assignedToNames,      // array of names (parallel to ids)
  emailNotification,
  triggeredByUid,
  triggeredByName,
}) {
  const ids = Array.isArray(assignedToIds) ? assignedToIds.filter(Boolean) : [];
  const names = Array.isArray(assignedToNames) ? assignedToNames : [];
  const primaryUid = ids[0] || null;
  const primaryName = names[0] || (primaryUid ? '' : '');

  const data = {
    companyId,
    title: description || (taskType === 'decision' ? 'Decision required' : 'Automated task'),
    description: '',
    // Automation tasks are tied to a job — populate both the new spec field
    // (jobId) and the existing convention (linkedType/linkedId) so nothing
    // existing breaks.
    jobId: jobId || null,
    linkedType: 'job',
    linkedId: jobId || null,
    linkedName: jobName || '',
    assignedTo: primaryUid,
    assignedToName: primaryName,
    assignedToIds: ids,
    assignedToNames: names,
    createdBy: triggeredByUid || 'system',
    createdByName: triggeredByName || 'Automation',
    dueDate: null,
    status: 'open',
    completedAt: null,
    isAutomated: true,
    type: taskType === 'decision' ? 'Decision' : 'Automated',
    automationRuleId: ruleId || null,
    automationEventId: eventId || null,
    emailNotification: !!emailNotification,
    pushHistory: [],
    automationDecisionNote: null,
    createdAt: serverTimestamp(),
  };

  const ref = await addDoc(collection(db, 'tasks'), data);
  return ref.id;
}

// Append a push entry to a task's pushHistory and reset the linked event so
// the afterDays scheduler will re-fire after the threshold from now.
export async function pushDecisionTask({ taskId, eventId, pushedBy, pushedByName, reason, nextFireAt }) {
  const ref = doc(db, 'tasks', taskId);
  const entry = {
    pushedAt: new Date(),
    pushedBy: pushedBy || null,
    pushedByName: pushedByName || '',
    reason: reason || '',
  };
  await updateDoc(ref, { pushHistory: arrayUnion(entry) });
  if (eventId) {
    await logAutomationEventPush(eventId, { pushedBy, reason });
    if (nextFireAt) await setAutomationEventNextFireAt(eventId, nextFireAt);
  }
}

// Mark a decision task as decided: store the note, mark Done, complete event.
export async function recordDecisionMade({ taskId, eventId, note }) {
  const ref = doc(db, 'tasks', taskId);
  await updateDoc(ref, {
    status: 'complete',
    completedAt: new Date(),
    automationDecisionNote: note || '',
    description: note || '',
  });
  if (eventId) await markAutomationEventCompleted(eventId);
}
